import { getRouteApi } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import DataTable from '~/components/DataTable';
import { LevelTags } from '~/partials/Tags';
import { useList } from '~/util/hooks/useList';
import { useLocaleString } from '~/util/hooks/useLocale';
import { LeaderboardClanEntry } from '~/util/types/leaderboard';

const Route = getRouteApi('/leaderboard/');

export const LeaderboardTableClans = () => {
    const data = Route.useLoaderData();
    const sort = Route.useSearch({
        select: ({ sort }) => sort,
    });
    const list = useList();

    const localeString = useLocaleString();

    const cellClassNames = (cell: { column: { id: string } }) =>
        `!clamp-[text-xs-base-clamp] p-2 ${
            (cell.column.id === 'country' ||
                cell.column.id === 'hardest' ||
                cell.column.id === 'extremes' ||
                cell.column.id === 'members_count' ||
                cell.column.id === 'points') &&
            'hidden lg:table-cell'
        }`;

    const clansColumns: ColumnDef<LeaderboardClanEntry>[] = [
        {
            accessorKey: 'rank',
            header: 'Rank',
            cell: ({ row }) => {
                const entry = row.original;
                const rank =
                    sort === 'TotalPoints' || sort === 'RawPoints'
                        ? `#${entry.rank}`
                        : sort === 'ExtremeCount'
                          ? `#${entry.extremes_rank}`
                          : sort === 'Hardest'
                            ? `#${entry.hardest_rank}`
                            : '-';

                return <h2 className="clamp-[text-lg-2xl-clamp]">{rank}</h2>;
            },
        },
        {
            accessorKey: 'clan.global_name',
            header: 'Clan',
            cell: ({ row }) => {
                return (
                    <>
                        <h3 className="hidden lg:block font-bold text-[1.3rem]">
                            [{row.original.clan.tag}]{' '}
                            {row.original.clan.global_name ===
                            row.original.clan.tag
                                ? ''
                                : row.original.clan.global_name}
                        </h3>
                        <div className="flex-col lg:hidden py-[0.2rem]">
                            <h3 className="font-bold text-[1.1rem]">
                                [{row.original.clan.tag}]{' '}
                                {row.original.clan.global_name ===
                                row.original.clan.tag
                                    ? ''
                                    : row.original.clan.global_name}
                            </h3>
                            <div className="h-1" />
                            <h3 className="text-secondary-light text-[1.2em]">
                                {localeString(row.original.level_points / 10)}{' '}
                                pts
                            </h3>
                        </div>
                    </>
                );
            },
        },
        {
            accessorKey: 'points',
            header: 'Points',
            cell: ({ row }) => {
                return (
                    <h3 className="text-secondary-light font-bold text-[1.2rem]">
                        {row.original.level_points / 10} points
                    </h3>
                );
            },
        },
        {
            accessorKey: 'extremes',
            header: () => <p className="text-center">Extremes count</p>,
            cell: ({ row }) => {
                const text = row.original.extremes
                    ? `${row.original.extremes} extreme${row.original.extremes === 1 ? '' : 's'}`
                    : '-';
                return (
                    <h3 className="text-secondary-light font-bold text-[1.2rem] text-center px-3">
                        {text}
                    </h3>
                );
            },
        },
        {
            accessorKey: 'hardest',
            header: 'Hardest',
            cell: ({ row }) => {
                const hardest = row.original.hardest;
                return hardest ? (
                    <LevelTags
                        item={hardest}
                        className="font-bold text-[1.2rem] text-secondary-light text-center bg-transparent"
                    />
                ) : (
                    <h3 className="text-secondary-light font-bold text-[1.2rem]">
                        -
                    </h3>
                );
            },
        },
        {
            accessorKey: 'members_count',
            header: 'Members',
            cell: ({ row }) => {
                return (
                    <h3 className="text-secondary-light font-bold text-[1.2rem]">
                        {row.original.members_count}
                    </h3>
                );
            },
        },
    ];

    return (
        <DataTable
            columns={clansColumns}
            data={(data?.data as LeaderboardClanEntry[]) || []}
            styledHeader
            cellClassName={cellClassNames}
            headerClassName={cellClassNames}
            onRowLink={({ data }) => ({
                to: '/profile/clan/$clanid',
                search: { list },
                params: { clanid: data.clan.id },
            })}
        />
    );
};
